<template>
  <div>
  <v-dialog
    v-model="internalValue"
    max-width="720px"
    scrollable
    transition="dialog-bottom-transition"
  >
    <v-card class="fit-card">
      <v-card-title class="fit-header">
        <v-icon color="white" class="mr-2">mdi-calendar-clock</v-icon>
        <span class="fit-title">Pendências de Encaixe</span>
        <v-spacer />
        <v-btn class="close-btn" icon color="white" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="ds-dialog-body">
        <div v-if="loading" class="text-center py-10">
          <v-progress-circular indeterminate color="primary" size="40" />
        </div>

        <section v-else-if="items.length === 0" class="ds-form-section fit-empty">
          <v-icon size="56" color="grey lighten-1">mdi-calendar-check</v-icon>
          <p class="body-1 grey--text mt-3 mb-0">
            Nenhuma pendência de encaixe.
          </p>
        </section>

        <section v-else class="ds-form-section">
          <div class="ds-form-section__head">
            <div class="ds-form-section__title">
              <v-icon small color="primary" class="mr-2">mdi-calendar-clock</v-icon>
              <span>Pendências</span>
            </div>
            <span class="ds-form-section__aside">{{ items.length }}</span>
          </div>
          <v-card
            v-for="fit in items"
            :key="fit.id"
            outlined
            class="fit-row mb-3"
          >
            <div class="fit-row-main">
              <div class="fit-patient">
                <v-icon small color="primary" class="mr-1">mdi-account</v-icon>
                <strong>{{ fit.patient ? fit.patient.name : "Paciente" }}</strong>
              </div>

              <div class="fit-detail">
                <span class="fit-label">Dias:</span>
                <v-chip
                  v-for="d in fit.weekdays"
                  :key="d"
                  x-small
                  color="primary"
                  outlined
                  class="mr-1"
                >
                  {{ weekdayLabel(d) }}
                </v-chip>
                <span v-if="!fit.weekdays || !fit.weekdays.length" class="grey--text">—</span>
              </div>

              <div class="fit-detail">
                <span class="fit-label">Horário:</span>
                <span>{{ timeRange(fit) }}</span>
              </div>

              <div class="fit-detail">
                <span class="fit-label">Atendimento agendado:</span>
                <span v-if="fit.event">
                  {{ formatDateTime(fit.event.start) }}
                </span>
                <span v-else class="grey--text">Sem agendamento vinculado</span>
              </div>

              <div v-if="fit.notes" class="fit-notes">{{ fit.notes }}</div>
            </div>

            <div class="fit-actions">
              <v-btn
                v-if="fit.event"
                small
                depressed
                color="primary"
                class="mr-1"
                @click="openReschedule(fit)"
              >
                <v-icon left small>mdi-calendar-edit</v-icon>
                Reagendar
              </v-btn>
              <v-btn
                small
                depressed
                color="success"
                :loading="actingId === fit.id"
                @click="resolve(fit)"
              >
                <v-icon left small>mdi-check</v-icon>
                Concluir
              </v-btn>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="error"
                    class="ml-1"
                    :loading="actingId === fit.id"
                    @click="remove(fit)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon small>mdi-trash-can-outline</v-icon>
                  </v-btn>
                </template>
                <span>Cancelar pedido</span>
              </v-tooltip>
            </div>
          </v-card>
        </section>
      </v-card-text>
    </v-card>
  </v-dialog>

    <!-- Reusa a tela de editar evento para reagendar (encaixar) o atendimento -->
    <CalendarDialogEditEvent
      v-model="editEventOpen"
      :event-to-edit="eventToEdit"
      @eventEdited="onRescheduled"
    />
  </div>
</template>

<script>
import { useSnackbar } from "@/composables/useSnackbar";
import {
  getPendingFitIns,
  resolveFitIn,
  deleteFitIn,
  WEEKDAYS,
} from "@/services/fitInRequestService";
import CalendarDialogEditEvent from "@/features/calendar/components/CalendarDialogEditEvent.vue";
import { formatDateTime } from "@/utils/datetime";

export default {
  name: "FitInPendingDialog",

  components: { CalendarDialogEditEvent },

  props: {
    value: { type: Boolean, default: false },
  },

  setup() {
    const { success, error, erroDeRequisicao } = useSnackbar();
    return { success, error, erroDeRequisicao };
  },

  data() {
    return {
      items: [],
      loading: false,
      actingId: null,
      editEventOpen: false,
      eventToEdit: null,
      reschedulingFit: null,
    };
  },

  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },

  watch: {
    value(open) {
      if (open) this.load();
    },
  },

  methods: {
    formatDateTime: (v) => formatDateTime(v, { fallback: "—" }),
    weekdayLabel(v) {
      return (WEEKDAYS.find((d) => d.value === v) || {}).label || v;
    },

    timeRange(fit) {
      const f = (fit.time_from || "").slice(0, 5);
      const t = (fit.time_to || "").slice(0, 5);
      if (f && t) return `${f} às ${t}`;
      if (f) return `a partir de ${f}`;
      if (t) return `até ${t}`;
      return "Qualquer horário";
    },

    async load() {
      this.loading = true;
      try {
        const res = await getPendingFitIns();
        this.items = res.items;
        this.$emit("count", res.pendingCount);
      } catch (err) {
        console.error("Erro ao carregar pendências de encaixe:", err);
        this.items = [];
      } finally {
        this.loading = false;
      }
    },

    async resolve(fit) {
      this.actingId = fit.id;
      try {
        await resolveFitIn(fit.id);
        this.success("Encaixe concluído.");
        await this.load();
        this.$emit("updated");
      } catch (err) {
        console.error("Erro ao concluir encaixe:", err);
        this.erroDeRequisicao(err, "Erro ao concluir o encaixe.");
      } finally {
        this.actingId = null;
      }
    },

    async remove(fit) {
      const ok = await this.$confirm({
        type: "delete",
        title: "Cancelar pedido de encaixe",
        message: `Remover o pedido de encaixe de "${fit.patient ? fit.patient.name : "paciente"}"?`,
        hint: "Esta ação não poderá ser desfeita.",
      });
      if (!ok) return;

      this.actingId = fit.id;
      try {
        await deleteFitIn(fit.id);
        await this.load();
        this.$emit("updated");
      } catch (err) {
        console.error("Erro ao remover encaixe:", err);
        this.erroDeRequisicao(err, "Erro ao remover o pedido.");
      } finally {
        this.actingId = null;
      }
    },

    openReschedule(fit) {
      if (!fit.event) return;
      this.reschedulingFit = fit;
      this.eventToEdit = {
        id: fit.event.id,
        google_id: fit.event.google_event_id,
        name: fit.patient ? fit.patient.name : "",
        start_iso: fit.event.start,
        end_iso: fit.event.end,
        time_zone: fit.event.time_zone,
        description: fit.event.description,
        status: fit.event.status,
      };
      this.editEventOpen = true;
    },

    async onRescheduled() {
      // Reagendou o atendimento -> conclui o encaixe automaticamente.
      const fit = this.reschedulingFit;
      this.reschedulingFit = null;
      if (fit) {
        try {
          await resolveFitIn(fit.id);
          this.success("Atendimento reagendado e encaixe concluído.");
        } catch (err) {
          console.error("Erro ao concluir encaixe após reagendamento:", err);
        }
      }
      await this.load();
      this.$emit("updated");
    },

    close() {
      this.internalValue = false;
    },
  },
};
</script>

<style scoped>
.fit-card {
  border-radius: 14px !important;
  overflow: hidden;
}
.fit-header {
  background: linear-gradient(135deg, var(--ds-primary) 0%, var(--ds-primary-deep) 100%);
  color: #fff;
  padding: 12px 16px;
}
.fit-title {
  font-size: 16px;
  font-weight: 700;
}
.fit-empty {
  text-align: center;
  padding: 40px 12px;
}
.fit-row {
  border-radius: 10px !important;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.fit-row-main {
  min-width: 0;
  flex: 1;
}
.fit-patient {
  display: flex;
  align-items: center;
  font-size: 14px;
  margin-bottom: 6px;
  color: var(--ds-text-strong, #1c1917);
}
.fit-detail {
  font-size: 12.5px;
  color: var(--ds-text-700, #44403c);
  margin-bottom: 3px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}
.fit-label {
  font-weight: 700;
  color: var(--ds-text-muted, var(--ds-text-muted));
  margin-right: 4px;
}
.fit-notes {
  font-size: 12px;
  font-style: italic;
  color: var(--ds-text-muted, var(--ds-text-muted));
  margin-top: 4px;
}
.fit-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
</style>
